import { UserPlus, MousePointerClick, ShoppingBag, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";

// Ciclo de cores da logo (amarelo → azul → verde → vermelho), classes estáticas.
const stepTones = [
  {
    icon: "bg-brand-yellow/15 text-brand-yellow-dark ring-brand-yellow/30",
    number: "text-brand-yellow-dark",
    line: "border-brand-blue/40",
  },
  {
    icon: "bg-brand-blue/10 text-brand-blue ring-brand-blue/25",
    number: "text-brand-blue",
    line: "border-brand-green/40",
  },
  {
    icon: "bg-brand-green/10 text-brand-green ring-brand-green/25",
    number: "text-brand-green",
    line: "border-brand-red/40",
  },
  {
    icon: "bg-brand-red/10 text-brand-red ring-brand-red/25",
    number: "text-brand-red",
    line: "",
  },
];

const stepKeys = ["register", "pickStore", "buy", "donate"] as const;
const stepIcons = [UserPlus, MousePointerClick, ShoppingBag, Heart];

export const HowItWorks = () => {
  const { t } = useTranslation("public");

  return (
    <section className="py-7 md:py-20 bg-background">
      <div className="container">
        <div className="text-center mb-6 md:mb-12">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-2">
            {t("howItWorks.title")}
          </h2>
          <p className="text-sm md:text-lg text-muted-foreground max-w-xl mx-auto">
            {t("howItWorks.subtitle")}
          </p>
        </div>

        {/* Mobile: trilha vertical compacta */}
        <ol className="md:hidden max-w-md mx-auto">
          {stepKeys.map((key, index) => {
            const Icon = stepIcons[index];
            const tone = stepTones[index];
            const last = index === stepKeys.length - 1;
            return (
              <li key={key} className={`relative flex gap-4 ${last ? "" : "pb-2"}`}>
                {!last && (
                  <span
                    aria-hidden
                    className={`absolute left-6 top-14 -bottom-1 border-l-2 border-dashed ${tone.line}`}
                  />
                )}
                <span
                  className={`relative z-10 grid h-12 w-12 shrink-0 place-items-center rounded-full ring-1 ${tone.icon}`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 pt-0.5">
                  <h3 className="font-display text-base font-bold leading-snug">
                    <span className={`mr-2 font-bold ${tone.number}`}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    {t(`howItWorks.steps.${key}.title`)}
                  </h3>
                  <p className="mt-1 text-sm leading-tight text-muted-foreground">
                    {t(`howItWorks.steps.${key}.description`)}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>

        {/* Desktop: trilha horizontal de 4 etapas */}
        <ol className="hidden md:grid md:grid-cols-4 md:gap-8 max-w-5xl mx-auto">
          {stepKeys.map((key, index) => {
            const Icon = stepIcons[index];
            const tone = stepTones[index];
            const last = index === stepKeys.length - 1;
            return (
              <li key={key} className="relative flex flex-col items-center text-center">
                {!last && (
                  <span
                    aria-hidden
                    className={`absolute top-9 left-[calc(50%+46px)] right-[calc(-50%+46px)] border-t-2 border-dashed ${tone.line}`}
                  />
                )}
                <span className="relative z-10 bg-background px-4">
                  <span
                    className={`grid h-[72px] w-[72px] place-items-center rounded-full ring-1 ${tone.icon}`}
                  >
                    <Icon className="h-8 w-8" />
                  </span>
                </span>
                <span className={`mt-4 font-display text-2xl font-bold ${tone.number}`}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-1 font-display text-lg font-bold leading-snug">
                  {t(`howItWorks.steps.${key}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-snug text-muted-foreground max-w-[15rem]">
                  {t(`howItWorks.steps.${key}.description`)}
                </p>
              </li>
            );
          })}
        </ol>

        <p className="mt-6 md:mt-12 text-center text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
          {t("howItWorks.closingLead")}
          <span className="font-semibold text-brand-yellow-dark">
            {t("howItWorks.closingHighlight")}
          </span>
          {t("howItWorks.closingTail")}
        </p>

      </div>
    </section>
  );
};
